import {
  BubbleChartRenderingLayer,
  defaultBubbleChartCommands,
  type BubbleChartCommands,
  type BubbleChartDataPointModel,
  type BubbleChartViewModelSourceData,
} from '@ardoq/bubble-chart';
import type {
  APIBubbleChartDataPoint,
  ArdoqId,
  BubbleChartRenderSettings,
} from '@ardoq/api-types';
import { componentUtils } from '@ardoq/scope-data';
import styled from 'styled-components';
import { useId, useMemo } from 'react';
import { buildBubbleChartSourceData } from './bubbleChart/buildBubbleChartSourceData';
import { useResizeDetector } from 'react-resize-detector';
import { noop } from 'lodash';

// The dashboard shows a fixed, fully-zoomed-out, read-only chart. Keep the
// rendering commands (sizing) and hover/focus, neutralize the pan/zoom gestures
// so the viewport stays pinned, and no-op the integration commands and
// event handlers.
const staticBubbleChartCommands: BubbleChartCommands = {
  ...defaultBubbleChartCommands,
  setScale: noop,
  setCenter: noop,
  panSettle: noop,
  selectComponent: noop,
  onBubbleClick: noop,
  onBubbleDoubleClick: noop,
};

const MeasureBox = styled.div`
  width: 100%;
  height: 100%;
`;

const Container = styled.div`
  display: flex;
  overflow: hidden;

  > * {
    height: 100%;
    width: 100%;
  }
`;

export type BubbleChartWidgetProps = BubbleChartRenderSettings & {
  _id: ArdoqId;
  data: APIBubbleChartDataPoint[];
  hasRadiusField: boolean;
  resolvedComponentColors?: Record<string, string>;
  resolvedLabels?: Record<string, string>;
};

const DEFAULT_UNIFORM_RADIUS = 12;

type ResolvedOverrides = {
  resolvedComponentColors?: Record<string, string>;
  resolvedLabels?: Record<string, string>;
  hasRadiusField: boolean;
};

const toRenderModel = (
  { typeId, ...point }: APIBubbleChartDataPoint,
  { resolvedComponentColors, resolvedLabels, hasRadiusField }: ResolvedOverrides
): BubbleChartDataPointModel => {
  const resolvedColor = resolvedComponentColors?.[point.cid];
  const resolvedLabel = resolvedLabels?.[point.cid];
  return {
    ...point,
    class: componentUtils.getComponentCSSClassNamesFromTypeId({
      componentId: point.cid,
      typeId,
    }),
    fill: point.fill || resolvedColor,
    stroke: point.stroke || resolvedColor,
    label: resolvedLabel ?? point.label,
    radius: hasRadiusField ? point.radius : DEFAULT_UNIFORM_RADIUS,
  };
};

const BubbleChartWidget = ({
  _id,
  data,
  background,
  customQuadrantSettings,
  showBubblesWithZeroValue,
  xAxisTitle,
  yAxisTitle,
  bubbleAreaScaleFactor,
  hasRadiusField,
  resolvedComponentColors,
  resolvedLabels,
}: BubbleChartWidgetProps) => {
  // Unique per instance: card and fullscreen modal share _id, so duplicate SVG clip-path ids cross-resolve via url(#…).
  const instanceId = useId();
  const viewInstanceId = `${_id}${instanceId}`;

  const { width, height, ref } = useResizeDetector<HTMLDivElement>({
    handleWidth: true,
    handleHeight: true,
    refreshMode: 'debounce',
    refreshRate: 50,
  });

  const sourceData = useMemo<BubbleChartViewModelSourceData>(
    () =>
      buildBubbleChartSourceData({
        viewInstanceId,
        data: data.map(point =>
          toRenderModel(point, {
            resolvedComponentColors,
            resolvedLabels,
            hasRadiusField,
          })
        ),
        background,
        customQuadrantSettings,
        showBubblesWithZeroValue,
        xAxisTitle,
        yAxisTitle,
        bubbleAreaScaleFactor,
      }),
    [
      viewInstanceId,
      data,
      resolvedComponentColors,
      resolvedLabels,
      hasRadiusField,
      background,
      customQuadrantSettings,
      showBubblesWithZeroValue,
      xAxisTitle,
      yAxisTitle,
      bubbleAreaScaleFactor,
    ]
  );

  const hasMeasured = typeof width === 'number' && typeof height === 'number';

  return (
    <MeasureBox ref={ref}>
      {hasMeasured ? (
        <Container style={{ width, height }}>
          <BubbleChartRenderingLayer
            viewInstanceId={viewInstanceId}
            sourceData={sourceData}
            viewSettingsForRenderingData={sourceData.viewSettings}
            bubbleChartCommands={staticBubbleChartCommands}
          />
        </Container>
      ) : null}
    </MeasureBox>
  );
};

export { BubbleChartWidget };
